import React, { ReactNode, useMemo } from "react";
import { observer } from "mobx-react-lite";

import useThemeService from "../../../services/theme-service/ThemeService";
import DisplayDescription from "../DisplayDescription";
import {
  TranslationContext,
  translationService,
} from "../../../services/translation/TranslationService";
import EditDescription from "../EditDescription";
import { Warning, Question } from "../../svg/svgImageList";
import Svg from "../../svg/Svg";

interface DataSectionProps {
  labelKey?: string;
  label?: string;
  labelContext?: TranslationContext;
  children?: ReactNode;
}

interface SubSectionProps {
  hideRightBorder?: boolean;
  className?: string;
  children?: ReactNode;
}

interface ActionHeaderProps {
  warningKey?: string;
  warningContext?: TranslationContext;
  isSticky?: boolean;
  noBorder?: boolean;
  children?: ReactNode;
  type?: "warning" | "question";
}

export function SectionDivider() {
  const { themeClass } = useThemeService();

  return <div className={themeClass(["section-divider"])} />;
}

export const DataSection = observer(
  ({ labelKey, label, labelContext, children }: DataSectionProps) => {
    const { themeClass } = useThemeService();

    let labelText = label ? label : "";

    if (labelKey)
      labelText = labelContext
        ? translationService.translate(labelKey, labelContext)
        : translationService.translate(labelKey);

    return (
      <>
        {labelText && <span className={themeClass(["formdata-section-label"])}>{labelText}</span>}
        <section className={themeClass(["formdata-section"])}>{children}</section>
      </>
    );
  }
);

interface SectionProps {
  children?: ReactNode;
}

export function ButtonsSection(props: SectionProps) {
  return <div className="buttons-header">{props.children}</div>;
}

export function FiltersSection(props: SectionProps) {
  return <div className="filters-header">{props.children}</div>;
}

export const ActionsSection = observer(
  ({
    children,
    warningKey,
    warningContext,
    isSticky = false,
    noBorder = false,
    type = "warning",
  }: ActionHeaderProps) => {
    const { themeClass } = useThemeService();
    const svgIcon = useMemo(() => {
      switch (type) {
        case "question":
          return Question;
        case "warning":
          return Warning;
      }
    }, [type]);

    return (
      <div className={`actions-container ${isSticky ? "is-sticky" : ""}`}>
        <section className={themeClass(["actions-header"])}>
          {warningKey && (
            <div
              id="actions-header-warning"
              className={`actions-header-warning ${noBorder ? "no-border" : ""}`}
            >
              <Svg svg={svgIcon} />
              {!warningContext && translationService.translate(warningKey)}
              {warningContext && translationService.translate(warningKey, warningContext)}
            </div>
          )}
          {children && <section className="actions">{children}</section>}
        </section>
      </div>
    );
  }
);

export function SubSection(props: SubSectionProps) {
  const { hideRightBorder, className, children } = props;
  const { themeClass } = useThemeService();
  return (
    <div
      className={themeClass(
        ["formdata-subsection", className ? className : ""],
        [hideRightBorder ? "no-right-border" : ""]
      )}
    >
      {children}
    </div>
  );
}

export function DescriptionSection({ description }: { description: string }) {
  return (
    <DataSection labelKey="general.description">
      <SubSection>
        <DisplayDescription description={description} />
      </SubSection>
    </DataSection>
  );
}

export function EditDescriptionSection({ description }: { description: string }) {
  return (
    <DataSection labelKey="general.description">
      <SubSection>
        <EditDescription description={description} />
      </SubSection>
    </DataSection>
  );
}
